import { Component, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LocaleService } from 'src/app/services/locale';
import { InviteService, LinkedCompanyDisplay } from 'src/app/services/invite';
import { CompanyService } from 'src/app/services/company';
import { UserService } from 'src/app/services/user';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { fetchUserAttributes } from 'aws-amplify/auth';

@Component({
  selector: 'app-emission',
  imports: [CommonModule, FormsModule],
  styles: [`
    @media (min-width: 640px) {
      .kpi-grid {
        grid-template-columns: repeat(5, minmax(0, 1fr)) !important;
      }
    }
  `],
  template: `
    <div class="px-2 sm:px-0">
      <!-- Page Header (Collapsible) -->
      <div class="bg-white border border-gray-100 rounded-lg shadow-sm overflow-hidden">
        <button
          (click)="isHeaderCollapsed = !isHeaderCollapsed"
          class="w-full flex items-center justify-between p-3 sm:p-4 cursor-pointer hover:bg-gray-50/50 transition-colors"
        >
          <div class="flex items-center gap-3">
            <h1 class="text-lg sm:text-xl futura text-[#043B22]">
              {{ locale.emission.heading }}
            </h1>
          </div>
          <svg
            class="w-5 h-5 text-[#043B22] transition-transform duration-300"
            [ngClass]="{ 'rotate-180': !isHeaderCollapsed }"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>
        <div
          class="overflow-hidden transition-all duration-300 ease-in-out"
          [ngClass]="isHeaderCollapsed ? 'max-h-0' : 'max-h-[200px]'"
        >
          <p class="px-3 sm:px-4 pb-3 sm:pb-4 text-xs sm:text-sm text-[#616161] leading-relaxed">
            {{ locale.emission.subheading }}
          </p>
        </div>
      </div>

      <!-- KPI Summary Cards -->
      <div *ngIf="selectedCompany" class="mt-4 grid grid-cols-2 gap-3 sm:gap-4 kpi-grid">
        <!-- Total Invited -->
        <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
            </div>
            <div>
              <p class="text-2xl font-bold text-[#043B22]">{{ kpiTotalInvited }}</p>
              <p class="text-[10px] sm:text-xs text-[#616161]">{{ locale.emission.total_invited }}</p>
            </div>
          </div>
        </div>

        <!-- Total Registered -->
        <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
              </svg>
            </div>
            <div>
              <p class="text-2xl font-bold text-[#043B22]">{{ kpiTotalRegistered }}</p>
              <p class="text-[10px] sm:text-xs text-[#616161]">{{ locale.emission.total_registered }}</p>
            </div>
          </div>
        </div>

        <!-- Total Started -->
        <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
            </div>
            <div>
              <p class="text-2xl font-bold text-[#043B22]">{{ kpiTotalStarted }}</p>
              <p class="text-[10px] sm:text-xs text-[#616161]">{{ locale.emission.total_started }}</p>
            </div>
          </div>
        </div>

        <!-- Total Completed -->
        <div class="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-shadow">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <div>
              <p class="text-2xl font-bold text-[#043B22]">{{ kpiTotalCompleted }}</p>
              <p class="text-[10px] sm:text-xs text-[#616161]">{{ locale.emission.total_completed }}</p>
            </div>
          </div>
        </div>

        <!-- Total Stakeholders -->
        <div class="bg-white rounded-xl border border-[#043B22]/20 shadow-sm p-4 hover:shadow-md transition-shadow">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-[#043B22]/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-[#043B22]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
            </div>
            <div>
              <p class="text-2xl font-bold text-[#043B22]">{{ kpiTotal }}</p>
              <p class="text-[10px] sm:text-xs text-[#616161]">{{ locale.emission.total }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Stakeholder Table Section -->
      <div class="mt-4 bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-xl">
        <!-- Section Header -->
        <div class="p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50/50 to-white">
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div class="flex items-center gap-3">
              <div>
                <h2 class="text-base sm:text-lg futura text-[#043B22]">{{ locale.emission.associated_companies }}</h2>
                <p class="text-xs sm:text-sm text-[#616161]">{{ locale.emission.associated_companies_desc }}</p>
              </div>
            </div>

            <!-- Invite Company Button -->
            <button
              (click)="openInviteModal()"
              [disabled]="!selectedCompany || !defaultInviteLink"
              [ngClass]="{'opacity-50 cursor-not-allowed': !selectedCompany || !defaultInviteLink}"
              class="flex items-center gap-2 px-4 sm:px-5 py-2 sm:py-2.5 bg-[#043B22] text-white futura text-xs sm:text-sm rounded-xl hover:bg-[#065a35] transition-all duration-300 shadow-sm hover:shadow-md cursor-pointer"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              {{ locale.emission.invite_company }}
            </button>
          </div>

          <!-- Company Selector (shows when no company selected) -->
          <div *ngIf="!selectedCompany" class="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-xl">
            <p class="text-xs sm:text-sm text-orange-700">
              {{ localeService.getSelectCompanyMessage('invites', hasCompanies) }}
            </p>
          </div>

          <!-- Default Invite Link Section (shows when company is selected) -->
          <div *ngIf="selectedCompany && defaultInviteLink" class="mt-4 p-3 sm:p-4 bg-[#043B22]/5 border border-[#043B22]/20 rounded-xl">
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div class="flex items-center gap-2">
                <div class="w-8 h-8 bg-[#043B22]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <svg class="w-4 h-4 text-[#043B22]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"/>
                  </svg>
                </div>
                <div>
                  <p class="text-xs sm:text-sm font-medium text-[#043B22]">{{ locale.emission.your_invite_link }}</p>
                  <p class="text-[10px] sm:text-xs text-[#616161]">{{ locale.emission.your_invite_link_desc }}</p>
                </div>
              </div>
            </div>

            <!-- Generated Link Display -->
            <div class="mt-3">
              <!-- Company Name Badge -->
              <div class="flex items-center gap-2 mb-2">
                <span class="text-[10px] sm:text-xs text-[#616161]">{{ getGeneratedForText() }}</span>
                <span class="px-2 py-0.5 bg-[#043B22]/10 text-[#043B22] text-[10px] sm:text-xs font-medium rounded-full">
                  {{ inviteLinkCompanyName }}
                </span>
              </div>
              <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-white rounded-lg border border-[#043B22]/20 p-2">
                <input
                  type="text"
                  [value]="defaultInviteLink"
                  readonly
                  class="flex-1 text-xs text-[#616161] bg-transparent border-none outline-none truncate px-2 py-1"
                />
                <button
                  (click)="copyDefaultLink()"
                  class="flex items-center gap-1 px-2.5 py-1.5 bg-[#043B22] text-white text-[10px] sm:text-xs rounded-lg hover:bg-[#065a35] transition-colors cursor-pointer flex-shrink-0"
                >
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
                  </svg>
                  {{ locale.emission.copy_link }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoadingLinkedCompanies" class="p-8 sm:p-12 text-center">
          <div class="flex flex-col items-center gap-4">
            <svg class="animate-spin h-8 w-8 text-[#043B22]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p class="text-sm text-[#616161]">{{ locale.common.loading }}</p>
          </div>
        </div>

        <!-- Table Content -->
        <div *ngIf="!isLoadingLinkedCompanies && linkedCompanies.length > 0">
          <!-- Mobile Card View -->
          <div class="block sm:hidden divide-y divide-gray-100">
            <div *ngFor="let company of linkedCompanies" class="p-4 hover:bg-gray-50/50 transition-colors">
              <div class="flex items-start justify-between mb-2">
                <div>
                  <p class="text-sm font-medium text-[#212121]" *ngIf="company.email">{{ company.email }}</p>
                  <p class="text-sm text-gray-400" *ngIf="!company.email">—</p>
                  <!-- Company Names -->
                  <div *ngIf="company.companyNames && company.companyNames.length > 0" class="flex flex-col gap-1 mt-1.5">
                    <div *ngFor="let name of company.companyNames" class="flex items-center gap-1.5">
                      <svg class="w-3.5 h-3.5 text-[#043B22] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                      </svg>
                      <p class="text-xs text-[#043B22] futura">{{ name }}</p>
                    </div>
                  </div>
                  <!-- Invite Date -->
                  <p class="text-[10px] text-[#616161] mt-1">{{ formatDate(company.createdAt) }}</p>
                </div>
                <span class="px-2 py-0.5 text-[10px] font-medium rounded-full flex-shrink-0"
                  [ngClass]="getStatusBadgeClass(company.status)">
                  {{ getStatusLabel(company.status) }}
                </span>
              </div>
              <div class="flex items-center justify-between mt-2">
                <span *ngIf="company.statusUpdatedAt" class="text-[10px] text-[#616161]">{{ formatDate(company.statusUpdatedAt) }}</span>
                <button
                  (click)="confirmRemove(company)"
                  class="text-red-600 hover:text-red-700 text-xs flex items-center gap-1 cursor-pointer"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                  </svg>
                  {{ locale.emission.remove }}
                </button>
              </div>
            </div>
          </div>

          <!-- Desktop Table View -->
          <div class="hidden sm:block overflow-x-auto p-4">
            <table class="w-full">
              <thead class="bg-[#043B22] text-white border-b border-gray-100">
                <tr>
                  <th class="px-4 lg:px-6 py-3 lg:py-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">{{ locale.emission.company_name }}</th>
                  <th class="px-4 lg:px-6 py-3 lg:py-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">{{ locale.emission.invite_date }}</th>
                  <th class="px-4 lg:px-6 py-3 lg:py-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">{{ locale.emission.status }}</th>
                  <th class="px-4 lg:px-6 py-3 lg:py-4 text-left text-[10px] sm:text-xs font-semibold uppercase tracking-wider">{{ locale.emission.last_activity }}</th>
                  <th class="px-4 lg:px-6 py-3 lg:py-4 text-right text-[10px] sm:text-xs font-semibold uppercase tracking-wider">{{ locale.emission.actions }}</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100">
                <tr *ngFor="let company of linkedCompanies" class="hover:bg-gray-50/50 transition-colors">
                  <!-- Company / Email -->
                  <td class="px-4 lg:px-6 py-3 lg:py-4">
                    <div>
                      <p class="text-xs sm:text-sm font-medium text-[#212121]">{{ company.email }}</p>
                      <div *ngIf="company.companyNames && company.companyNames.length > 0" class="flex flex-col gap-1 mt-1">
                        <div *ngFor="let name of company.companyNames" class="flex items-center gap-1.5">
                          <svg class="w-3.5 h-3.5 text-[#043B22] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                          </svg>
                          <span class="text-xs text-[#043B22] font-medium">{{ name }}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                  <!-- Invite Date -->
                  <td class="px-4 lg:px-6 py-3 lg:py-4 text-xs sm:text-sm text-[#616161]">
                    {{ formatDate(company.createdAt) }}
                  </td>
                  <!-- Status Badge -->
                  <td class="px-4 lg:px-6 py-3 lg:py-4">
                    <span class="px-2 sm:px-2.5 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium rounded-full"
                      [ngClass]="getStatusBadgeClass(company.status)">
                      {{ getStatusLabel(company.status) }}
                    </span>
                  </td>
                  <!-- Last Activity -->
                  <td class="px-4 lg:px-6 py-3 lg:py-4 text-xs sm:text-sm text-[#616161]">
                    <span *ngIf="company.statusUpdatedAt">{{ formatDate(company.statusUpdatedAt) }}</span>
                    <span *ngIf="!company.statusUpdatedAt" class="text-gray-400">—</span>
                  </td>
                  <!-- Actions -->
                  <td class="px-4 lg:px-6 py-3 lg:py-4 text-right">
                    <button
                      (click)="confirmRemove(company)"
                      class="text-red-600 hover:text-red-700 text-xs sm:text-sm font-medium flex items-center gap-1.5 ml-auto cursor-pointer hover:underline"
                    >
                      <svg class="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                      {{ locale.emission.remove }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Empty State -->
        <div *ngIf="!isLoadingLinkedCompanies && linkedCompanies.length === 0" class="p-8 sm:p-12 lg:p-16 text-center">
          <div class="w-16 h-16 sm:w-20 sm:h-20 bg-[#043B22]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg class="w-8 h-8 sm:w-10 sm:h-10 text-[#043B22]/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
            </svg>
          </div>
          <h3 class="text-base sm:text-lg futura text-[#043B22] mb-2">{{ locale.emission.no_linked_companies }}</h3>
          <p class="text-xs sm:text-sm text-[#616161] max-w-md mx-auto">{{ locale.emission.no_linked_companies_desc }}</p>
        </div>
      </div>

      <!-- Invite Modal -->
      <div
        *ngIf="showInviteModal"
        class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
        (click)="closeInviteModal()"
      >
        <div
          class="bg-white rounded-2xl w-full max-w-[400px] sm:max-w-md shadow-2xl overflow-hidden transform transition-all"
          (click)="$event.stopPropagation()"
        >
          <!-- Modal Header -->
          <div class="p-4 sm:p-5 border-b border-gray-100 bg-gradient-to-r from-[#043B22] to-[#065a35]">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"/>
                </svg>
              </div>
              <div>
                <h3 class="futura text-base sm:text-lg text-white">{{ locale.emission.invite_company }}</h3>
                <p class="text-xs text-white/80">{{ locale.emission.invite_company_desc }}</p>
              </div>
            </div>
          </div>

          <!-- Modal Content -->
          <div class="p-4 sm:p-5">
            <!-- Email Input -->
            <div class="mb-4">
              <label class="block text-xs sm:text-sm font-medium text-[#043B22] mb-1.5">
                {{ locale.emission.invitee_email }}
              </label>
              <input
                type="email"
                [(ngModel)]="inviteeEmail"
                [placeholder]="getEmailPlaceholder()"
                class="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#043B22] transition-colors"
                [ngClass]="{'border-red-400': emailError}"
              />
              <p *ngIf="emailError" class="text-xs text-red-500 mt-1">{{ emailError }}</p>
            </div>

            <!-- Info Text -->
            <p class="text-xs text-[#616161] mb-4">
              {{ getInviteInfoText() }}
            </p>
          </div>

          <!-- Modal Actions -->
          <div class="p-4 sm:p-5 pt-0 flex gap-2 sm:gap-3">
            <button
              (click)="closeInviteModal()"
              class="flex-1 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium text-[#616161] bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-xl transition-all cursor-pointer"
            >
              {{ getCancelText() }}
            </button>
            <button
              (click)="sendInvitation()"
              [disabled]="!inviteeEmail || isSendingInvite"
              [ngClass]="{'opacity-50 cursor-not-allowed': !inviteeEmail || isSendingInvite}"
              class="flex-1 px-3 sm:px-4 py-2.5 text-xs sm:text-sm font-medium text-white bg-[#043B22] hover:bg-[#065a35] rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2"
            >
              <svg *ngIf="isSendingInvite" class="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <svg *ngIf="!isSendingInvite" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
              </svg>
              {{ locale.emission.send_invite }}
            </button>
          </div>
        </div>
      </div>

      <!-- Remove Confirmation Modal -->
      <div
        *ngIf="showRemoveModal"
        class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-4"
        (click)="showRemoveModal = false"
      >
        <div
          class="bg-white rounded-2xl w-full max-w-[320px] sm:max-w-sm shadow-2xl overflow-hidden transform transition-all"
          (click)="$event.stopPropagation()"
        >
          <!-- Modal Icon -->
          <div class="pt-5 sm:pt-6 pb-3 sm:pb-4 flex justify-center">
            <div class="w-14 h-14 sm:w-16 sm:h-16 bg-red-100 rounded-full flex items-center justify-center">
              <div class="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center shadow-lg shadow-red-500/30">
                <svg class="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
              </div>
            </div>
          </div>

          <!-- Modal Content -->
          <div class="px-4 sm:px-6 pb-2 text-center">
            <h3 class="futura text-base sm:text-lg text-[#043B22] mb-1.5 sm:mb-2">{{ locale.emission.remove }}</h3>
            <p class="text-xs sm:text-sm text-[#616161] leading-relaxed">{{ locale.emission.remove_confirm }}</p>
          </div>

          <!-- Modal Actions -->
          <div class="p-4 sm:p-5 pt-3 sm:pt-4 flex gap-2 sm:gap-3">
            <button
              (click)="showRemoveModal = false"
              class="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-[#616161] bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-xl transition-all cursor-pointer"
            >
              {{ getCancelText() }}
            </button>
            <button
              (click)="removeCompany()"
              class="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-xl transition-all cursor-pointer shadow-md shadow-red-500/30"
            >
              {{ locale.emission.remove }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `
})
export class EmissionComponent {
  localeService = inject(LocaleService);
  inviteService = inject(InviteService);
  companyService = inject(CompanyService);
  userService = inject(UserService);
  toastr = inject(ToastrService);
  spinner = inject(NgxSpinnerService);

  locale = this.localeService.locale();
  language = this.localeService.getLanguage();

  isHeaderCollapsed = true;

  selectedCompany: any = null;
  hasCompanies = false;
  currentUser: any = null;
  currentUserEmail: string = '';
  linkedCompanies: LinkedCompanyDisplay[] = [];
  defaultInviteLink: string = '';
  inviteLinkCompanyName: string = '';
  showRemoveModal = false;
  companyToRemove: LinkedCompanyDisplay | null = null;

  // KPI counts
  kpiTotalInvited = 0;
  kpiTotalRegistered = 0;
  kpiTotalStarted = 0;
  kpiTotalCompleted = 0;
  kpiTotal = 0;

  // Invite modal properties
  showInviteModal = false;
  inviteeEmail: string = '';
  emailError: string = '';
  isSendingInvite = false;
  isLoadingLinkedCompanies = false;

  constructor() {
    this.loadCurrentUserEmail();

    effect(() => {
      this.locale = this.localeService.locale();
      this.language = this.localeService.getLanguage();
      this.selectedCompany = this.companyService.company();
      this.hasCompanies = (this.companyService.companies()?.length ?? 0) > 0;
      this.currentUser = this.userService.user();

      // Auto-generate invite link when company is selected
      if (this.selectedCompany && this.currentUser) {
        if (!this.defaultInviteLink) {
          this.generateDefaultLinkOnly();
        }
        // Load invites for the current user
        this.loadLinkedCompanies();
      }
    });
  }

  async loadCurrentUserEmail(): Promise<void> {
    try {
      const userAttributes = await fetchUserAttributes();
      this.currentUserEmail = userAttributes.email || '';
    } catch (error) {
      console.error('Error fetching user email:', error);
    }
  }

  async loadLinkedCompanies(): Promise<void> {
    if (this.selectedCompany?.id) {
      this.isLoadingLinkedCompanies = true;

      try {
        await this.inviteService.loadInvitesForCompanyDisplay(this.selectedCompany.id);

        // Get associated company IDs for name resolution
        const associatedCompanyIds = this.inviteService.getAssociatedCompanyIds();

        // Build a map of company IDs to names
        const companyNamesMap = new Map<string, string>();

        // Add companies from the current user's company list
        const allCompanies = this.companyService.companies() || [];
        allCompanies.forEach((c: any) => {
          companyNamesMap.set(c.id, c.companyName);
        });

        // Fetch company names from API for IDs not in the current user's list
        const idsToFetch = associatedCompanyIds.filter(id => !companyNamesMap.has(id));
        if (idsToFetch.length > 0) {
          const fetchPromises = idsToFetch.map(async (id) => {
            const name = await this.inviteService.fetchCompanyName(id);
            if (name) {
              companyNamesMap.set(id, name);
            }
          });
          await Promise.all(fetchPromises);
        }

        this.linkedCompanies = this.inviteService.getLinkedCompaniesDisplay(companyNamesMap);
        this.updateKPIs();
      } finally {
        this.isLoadingLinkedCompanies = false;
      }
    }
  }

  updateKPIs(): void {
    this.kpiTotalInvited = this.linkedCompanies.filter(c => c.status === 'invited').length;
    this.kpiTotalRegistered = this.linkedCompanies.filter(c => c.status === 'registered').length;
    this.kpiTotalStarted = this.linkedCompanies.filter(c => c.status === 'started').length;
    this.kpiTotalCompleted = this.linkedCompanies.filter(c => c.status === 'completed').length;
    this.kpiTotal = this.linkedCompanies.length;
  }

  openInviteModal(): void {
    if (!this.selectedCompany || !this.defaultInviteLink) return;
    this.showInviteModal = true;
    this.inviteeEmail = '';
    this.emailError = '';
  }

  closeInviteModal(): void {
    this.showInviteModal = false;
    this.inviteeEmail = '';
    this.emailError = '';
    this.isSendingInvite = false;
  }

  generateDefaultLinkOnly(): void {
    if (!this.selectedCompany || !this.currentUser) return;
    this.defaultInviteLink = this.inviteService.generateInviteLink(this.selectedCompany.id, this.selectedCompany.companyName);
    this.inviteLinkCompanyName = this.selectedCompany.companyName;
  }

  copyDefaultLink(): void {
    navigator.clipboard.writeText(this.defaultInviteLink).then(() => {
      this.toastr.success(this.locale.emission.link_copied);
    });
  }

  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  async sendInvitation(): Promise<void> {
    if (!this.selectedCompany || !this.currentUser) return;

    // Validate email
    if (!this.inviteeEmail) {
      this.emailError = this.language === 'english' ? 'Email is required' : 'Email richiesta';
      return;
    }

    if (!this.validateEmail(this.inviteeEmail)) {
      this.emailError = this.language === 'english' ? 'Please enter a valid email' : 'Inserisci un\'email valida';
      return;
    }

    // Check if user is trying to invite themselves
    if (this.inviteeEmail.toLowerCase() === this.currentUserEmail.toLowerCase()) {
      this.emailError = this.language === 'english' ? 'You cannot invite yourself' : 'Non puoi invitare te stesso';
      return;
    }

    this.emailError = '';
    this.isSendingInvite = true;

    // Check if email belongs to an already registered user
    const isRegistered = await this.inviteService.isRegisteredUser(this.inviteeEmail);
    if (isRegistered) {
      this.emailError = this.language === 'english'
        ? 'This email belongs to an already registered user on the platform.'
        : 'Questa email appartiene a un utente già registrato sulla piattaforma.';
      this.isSendingInvite = false;
      return;
    }

    try {
      // Validate that user email exists for sending notification emails
      if (!this.currentUserEmail) {
        const noUserEmailMsg = this.language === 'english'
          ? 'Unable to retrieve your email. Please try again.'
          : 'Impossibile recuperare la tua email. Riprova.';
        this.toastr.error(noUserEmailMsg);
        this.isSendingInvite = false;
        return;
      }

      // Use the existing default invite link
      if (!this.defaultInviteLink) {
        this.generateDefaultLinkOnly();
      }

      // Create invite entry in DynamoDB
      const invite = await this.inviteService.createInvite(
        this.currentUser.id,
        this.selectedCompany.id,
        this.inviteeEmail,
        this.currentUserEmail
      );

      if (!invite) {
        throw new Error('Failed to create invite');
      }

      // Send invitation email via API with the default link
      await this.inviteService.sendInvitationEmail(
        this.inviteeEmail,
        this.selectedCompany.companyName,
        this.defaultInviteLink
      );

      await this.loadLinkedCompanies();
      this.toastr.success(this.locale.emission.invite_sent);
      this.closeInviteModal();
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      if (error?.message === 'DUPLICATE_INVITE') {
        const duplicateMsg = this.language === 'english'
          ? 'An invitation has already been sent to this email address.'
          : 'Un invito è già stato inviato a questo indirizzo email.';
        this.toastr.error(duplicateMsg);
      } else {
        this.toastr.error(this.locale.emission.invite_error);
      }
    } finally {
      this.isSendingInvite = false;
    }
  }

  getEmailPlaceholder(): string {
    return this.language === 'english' ? 'Enter email address' : 'Inserisci indirizzo email';
  }

  getInviteInfoText(): string {
    return this.language === 'english'
      ? 'An invitation email with your invite link will be sent to this address.'
      : 'Un\'email di invito con il tuo link di invito verrà inviata a questo indirizzo.';
  }

  getGeneratedForText(): string {
    return this.language === 'english' ? 'Generated for:' : 'Generato per:';
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'invited':
        return this.locale.emission.status_invited;
      case 'registered':
      case 'accepted':
        return this.locale.emission.status_registered;
      case 'started':
        return this.locale.emission.status_started;
      case 'completed':
        return this.locale.emission.status_completed;
      default:
        return status;
    }
  }

  getStatusBadgeClass(status: string): Record<string, boolean> {
    return {
      'bg-gray-100 text-gray-700': status === 'invited',
      'bg-blue-100 text-blue-700': status === 'registered' || status === 'accepted',
      'bg-orange-100 text-orange-700': status === 'started',
      'bg-green-100 text-green-700': status === 'completed'
    };
  }

  formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(this.language === 'english' ? 'en-GB' : 'it-IT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  }

  getCancelText(): string {
    return this.language === 'english' ? 'Cancel' : 'Annulla';
  }

  confirmRemove(company: LinkedCompanyDisplay): void {
    this.companyToRemove = company;
    this.showRemoveModal = true;
  }

  async removeCompany(): Promise<void> {
    if (this.companyToRemove) {
      const success = await this.inviteService.deleteInvite(this.companyToRemove.inviteId);
      if (success) {
        await this.loadLinkedCompanies();
        const successMsg = this.language === 'english'
          ? 'Invite removed successfully.'
          : 'Invito rimosso con successo.';
        this.toastr.success(successMsg);
      }
      this.showRemoveModal = false;
      this.companyToRemove = null;
    }
  }
}
